/**
 * 사이드 메뉴 / 드래그 핸들 (P7-③) — 호버한 블록 좌측에 뜨는 컨트롤.
 *  ⠿ 핸들(그립): 클릭 시 드롭다운(위/아래 추가·삭제·표 정렬), 네이티브 DnD로 블록 재정렬.
 *  (블록 추가 "+" 버튼은 스펙에서 제거 — 추가는 그립 드롭다운으로만.)
 * 최상위 .block만 대상. 오버레이는 document.body에 절대배치(스크롤 보정).
 */

import { makeBlockElement, directContent } from "../serialize/block.js";

const DEFAULT_PROPS = () => ({ textColor: "default", backgroundColor: "default", textAlignment: "left" });

function focusContent(content) {
  if (!content) return;
  try {
    const r = document.createRange();
    r.selectNodeContents(content);
    r.collapse(true);
    const s = window.getSelection();
    s.removeAllRanges();
    s.addRange(r);
    content.focus && content.focus();
  } catch (_) {}
}

export function attachSideMenu(editorEl, opts) {
  opts = opts || {};
  const onChange = typeof opts.onChange === "function" ? opts.onChange : () => {};
  const onDuplicate = typeof opts.onDuplicate === "function" ? opts.onDuplicate : null;

  const menu = document.createElement("div");
  menu.className = "lumir-side-menu";
  menu.style.position = "absolute";
  menu.style.display = "none";
  menu.innerHTML = '<button type="button" class="lsm-btn lsm-drag" title="끌어서 이동" draggable="true">⠿</button>';
  document.body.appendChild(menu);

  const indicator = document.createElement("div");
  indicator.className = "lumir-drop-indicator";
  indicator.style.position = "absolute";
  indicator.style.display = "none";
  indicator.style.pointerEvents = "none"; // elementFromPoint가 인디케이터를 집지 않도록
  document.body.appendChild(indicator);

  const dragMenu = document.createElement("div");
  dragMenu.className = "lumir-drag-menu";
  dragMenu.style.position = "absolute";
  dragMenu.style.display = "none";
  document.body.appendChild(dragMenu);

  let currentBlock = null;
  let dragSrc = null;
  let dragEdge = null; // { block, side } — 가장자리 드래그 → 2단 변환 모드
  let dropInto = null; // .lumir-column — 기존 2단의 특정 컬럼 안으로 드롭 모드
  const EDGE_PX = 48;

  function topBlock(node) {
    const el = node && node.nodeType === 1 ? node : node && node.parentElement;
    if (!el) return null;
    // 최내곽 .block 반환 → 컬럼(2단) 내부 등 중첩 블록도 그립 표시.
    // (컬럼 패딩/간격에 호버하면 가장 가까운 .block = columnList 가 잡혀 컬럼리스트 그립도 동작)
    const b = el.closest(".block");
    return b && editorEl.contains(b) ? b : null;
  }
  function showFor(block) {
    if (!block) return;
    currentBlock = block;
    menu.style.display = "flex"; // 표시 후 측정(offsetWidth 0 방지)
    const r = block.getBoundingClientRect();
    const er = editorEl.getBoundingClientRect ? editorEl.getBoundingClientRect() : { left: 0 };
    const mw = menu.offsetWidth || 48;
    let left = window.scrollX + r.left - mw - 6;
    const minLeft = window.scrollX + Math.max(2, er.left); // 에디터 좌측 경계(거터) 밖으로 나가지 않음
    if (left < minLeft) left = minLeft;
    menu.style.top = window.scrollY + r.top + 4 + "px"; // 그립 위치(블록 상단 기준 4px 아래)
    menu.style.left = left + "px";
  }
  function hide() {
    menu.style.display = "none";
  }

  function addAfter(block) {
    if (!block) return null;
    const p = makeBlockElement("paragraph", { props: DEFAULT_PROPS(), innerHTML: "" });
    block.after(p);
    focusContent(directContent(p));
    onChange();
    return p;
  }
  /** src 블록을 target 블록 앞/뒤로 이동 */
  function moveBlock(src, target, placeAfter) {
    if (!src || !target || src === target) return false;
    if (placeAfter) target.after(src);
    else target.before(src);
    onChange();
    return true;
  }

  // --- 호버로 메뉴 추적 ---
  function onMove(e) {
    if (dragSrc) return; // 드래그 중엔 위치 고정
    let b = topBlock(e.target);
    if (!b) {
      // 좌측 거터(.lumir-editor padding-left:24px, 그립이 뜨는 자리)에 호버하면 e.target 이 .block 이 아니라
      // 그립이 안 떴다 → 커서 Y 로 해당 행의 최상위 블록을 찾아 표시(블록 콘텐츠 밖에서도 그립 노출).
      const hit = topBlockAt(e.clientX, e.clientY);
      b = hit && hit.block;
    }
    if (!b) return;
    // sticky: 현재 안쪽 블록의 "조상 컨테이너"(예: 2단 columnList)로 승격되지 않게 유지.
    // 안쪽 블록 그립은 컬럼 왼쪽 패딩 위에 뜨는데, 그 패딩은 .block이 아닌 .lumir-column
    // 소유라 closest(".block")가 columnList로 잡힘 → 그립을 향해 커서를 옮기면 columnList로
    // 튀어 클릭 불가. 조상 컨테이너로의 전환은 무시해 안쪽 그립을 클릭 가능하게 한다.
    if (
      menu.style.display !== "none" &&
      currentBlock &&
      b !== currentBlock &&
      editorEl.contains(currentBlock) &&
      b.contains(currentBlock)
    ) {
      return;
    }
    showFor(b);
  }
  editorEl.addEventListener("mousemove", onMove);
  function onMouseLeave(e) { if (!menu.contains(e.relatedTarget)) hide(); }
  editorEl.addEventListener("mouseleave", onMouseLeave);

  menu.addEventListener("mousedown", (e) => { if (!e.target.closest(".lsm-drag")) e.preventDefault(); });

  // --- 드래그핸들 드롭다운 메뉴 (T3-⑤) ---
  function closeDragMenu() { dragMenu.style.display = "none"; dragMenu.__block = null; }
  function openDragMenu(block) {
    if (!block) return;
    const isTable = block.dataset.type === "table";
    dragMenu.innerHTML =
      '<button type="button" class="ldm-item" data-act="addAbove">위에 블록 추가</button>' +
      '<button type="button" class="ldm-item" data-act="addBelow">아래에 블록 추가</button>' +
      (onDuplicate ? '<button type="button" class="ldm-item" data-act="duplicate">블록 복제</button>' : "") +
      (isTable ? '<div class="ldm-sep"></div><button type="button" class="ldm-item" data-act="tblL">표 왼쪽 정렬</button><button type="button" class="ldm-item" data-act="tblC">표 가운데 정렬</button><button type="button" class="ldm-item" data-act="tblR">표 오른쪽 정렬</button>' : "") +
      '<div class="ldm-sep"></div><button type="button" class="ldm-item danger" data-act="delete">삭제</button>';
    const r = menu.getBoundingClientRect ? menu.getBoundingClientRect() : { bottom: 0, left: 0 };
    dragMenu.style.top = window.scrollY + (r.bottom || 0) + 4 + "px";
    dragMenu.style.left = window.scrollX + (r.left || 0) + "px";
    dragMenu.style.display = "block";
    dragMenu.__block = block;
  }
  dragMenu.addEventListener("mousedown", (e) => e.preventDefault());
  dragMenu.addEventListener("click", (e) => {
    const it = e.target.closest(".ldm-item");
    if (!it) return;
    const block = dragMenu.__block;
    if (!block) { closeDragMenu(); return; }
    const act = it.dataset.act;
    if (act === "addAbove") { const p = makeBlockElement("paragraph", { props: DEFAULT_PROPS(), innerHTML: "" }); block.before(p); focusContent(directContent(p)); onChange(); }
    else if (act === "addBelow") { addAfter(block); }
    else if (act === "duplicate") { if (onDuplicate) onDuplicate(block); }
    else if (act === "delete") { const host = block.parentElement; block.remove(); if (host && !host.querySelector(":scope > .block")) host.appendChild(makeBlockElement("paragraph", { props: DEFAULT_PROPS(), innerHTML: "" })); onChange(); }
    else if (act === "tblL" || act === "tblC" || act === "tblR") {
      const v = act === "tblL" ? "left" : act === "tblC" ? "center" : "right";
      if (!block.__props) block.__props = {};
      if (v !== "left") { block.__props.tableAlignment = v; block.dataset.tableAlignment = v; } else { delete block.__props.tableAlignment; block.removeAttribute("data-table-alignment"); }
      onChange();
    }
    closeDragMenu();
  });
  const onDocDown = (e) => { if (!e.target.closest(".lumir-drag-menu") && !e.target.closest(".lsm-drag")) closeDragMenu(); };
  document.addEventListener("mousedown", onDocDown);

  // --- 드래그 재정렬 ---
  const handle = menu.querySelector(".lsm-drag");
  handle.addEventListener("click", (e) => { e.stopPropagation(); if (dragMenu.style.display === "block") closeDragMenu(); else openDragMenu(currentBlock); });
  handle.addEventListener("dragstart", (e) => {
    dragSrc = currentBlock;
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      try { e.dataTransfer.setData("application/x-lumir-block", "1"); } catch (_) {}
    }
  });
  handle.addEventListener("dragend", () => {
    dragSrc = null;
    dragEdge = null;
    dropInto = null;
    indicator.style.display = "none";
  });

  /** 지정 컨테이너 내 블록들 중 clientY 기준 삽입 지점 */
  function dropTargetIn(container, clientY) {
    const blocks = [...container.children].filter((b) => b.classList && b.classList.contains("block"));
    for (const b of blocks) {
      const r = b.getBoundingClientRect();
      if (clientY < r.top + r.height / 2) return { block: b, after: false };
    }
    const last = blocks[blocks.length - 1];
    return { block: last || null, after: true };
  }
  function dropTarget(clientY) {
    // 재정렬 후보는 드래그 중인 블록과 같은 컨테이너(최상위 또는 컬럼 내부)로 한정
    const container = dragSrc && dragSrc.parentElement ? dragSrc.parentElement : editorEl;
    const t = dropTargetIn(container, clientY);
    return t.block ? t : null;
  }
  /** 커서 아래의 컬럼(.lumir-column) — 기존 2단의 특정 컬럼으로 드롭 판별 */
  function columnUnder(x, y) {
    const el = typeof document.elementFromPoint === "function" ? document.elementFromPoint(x, y) : null;
    const col = el && el.closest ? el.closest(".lumir-column") : null;
    return col && editorEl.contains(col) ? col : null;
  }
  function showIndicator(t) {
    if (!t || !t.block) { indicator.style.display = "none"; return; }
    const r = t.block.getBoundingClientRect();
    indicator.style.display = "block";
    indicator.style.height = "2px";
    indicator.style.top = window.scrollY + (t.after ? r.bottom : r.top) - 1 + "px";
    indicator.style.left = window.scrollX + r.left + "px";
    indicator.style.width = r.width + "px";
  }
  function showColumnTopIndicator(col) {
    const r = col.getBoundingClientRect();
    indicator.style.display = "block";
    indicator.style.height = "2px";
    indicator.style.top = window.scrollY + r.top + 4 + "px";
    indicator.style.left = window.scrollX + r.left + "px";
    indicator.style.width = r.width + "px";
  }
  // ── 가장자리 드래그 → 2단 변환 (P9-⑦) ──
  function topBlockAt(x, y) {
    const blocks = [...editorEl.children].filter((b) => b.classList && b.classList.contains("block"));
    for (const b of blocks) { const r = b.getBoundingClientRect(); if (y >= r.top && y <= r.bottom) return { block: b, rect: r }; }
    return null;
  }
  function edgeOf(hit, x) {
    if (!hit) return null;
    if (x <= hit.rect.left + EDGE_PX) return "left";
    if (x >= hit.rect.right - EDGE_PX) return "right";
    return null;
  }
  function showVIndicator(rect, side) {
    indicator.style.display = "block";
    indicator.style.width = "3px";
    indicator.style.height = rect.height + "px";
    indicator.style.top = window.scrollY + rect.top + "px";
    indicator.style.left = window.scrollX + (side === "left" ? rect.left : rect.right) - 1 + "px";
  }
  function mkColumn() {
    const c = document.createElement("div");
    c.className = "lumir-column";
    c.dataset.type = "column";
    c.__props = { width: 1 };
    c.style.flexGrow = "1";
    c.style.flexBasis = "0";
    c.style.flexShrink = "1";
    return c;
  }
  /** src·target 두 블록을 2단 columnList로 감싼다(side: src가 놓일 쪽) */
  function wrapInColumns(src, target, side) {
    if (!src || !target || src === target) return false;
    const cl = document.createElement("div");
    cl.className = "block";
    cl.dataset.type = "columnList";
    cl.__props = {};
    const content = document.createElement("div");
    content.className = "content lumir-columns";
    content.setAttribute("contenteditable", "false");
    const colA = mkColumn();
    const colB = mkColumn();
    const handle = document.createElement("div");
    handle.className = "lumir-col-resize";
    handle.setAttribute("contenteditable", "false");
    target.before(cl); // columnList를 target 위치에 삽입
    const left = side === "left" ? src : target;
    const right = side === "left" ? target : src;
    colA.appendChild(left);
    colB.appendChild(right);
    content.appendChild(colA);
    content.appendChild(handle);
    content.appendChild(colB);
    cl.appendChild(content);
    onChange();
    return true;
  }
  function onDragOver(e) {
    if (!dragSrc) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    // 1) 기존 2단 컬럼의 특정 컬럼 위 → 그 컬럼 안으로 삽입(좌/우 원하는 컬럼).
    const overCol = columnUnder(e.clientX, e.clientY);
    if (overCol && !dragSrc.contains(overCol)) {
      dragEdge = null; dropInto = overCol;
      const t = dropTargetIn(overCol, e.clientY);
      if (t.block) showIndicator(t);
      else showColumnTopIndicator(overCol); // 빈 컬럼: 상단 표시
      return;
    }
    dropInto = null;
    // 2) 최상위 블록 가장자리 → 새 2단 생성(2단까지만, columnList 대상 제외)
    const hit = topBlockAt(e.clientX, e.clientY);
    const topLevelSrc = dragSrc && dragSrc.parentElement === editorEl;
    const canEdge = hit && hit.block !== dragSrc && topLevelSrc && hit.block.dataset.type !== "columnList" && dragSrc.dataset.type !== "columnList";
    const side = canEdge ? edgeOf(hit, e.clientX) : null;
    if (side) { dragEdge = { block: hit.block, side }; showVIndicator(hit.rect, side); return; }
    dragEdge = null;
    // 3) 같은 컨테이너 재정렬
    showIndicator(dropTarget(e.clientY));
  }
  editorEl.addEventListener("dragover", onDragOver);
  function onDrop(e) {
    if (!dragSrc) return;
    e.preventDefault();
    if (dropInto && !dragSrc.contains(dropInto)) {
      const t = dropTargetIn(dropInto, e.clientY);
      if (t.block && t.block !== dragSrc) moveBlock(dragSrc, t.block, t.after);
      else if (!t.block) { dropInto.appendChild(dragSrc); onChange(); }
    } else if (dragEdge) {
      wrapInColumns(dragSrc, dragEdge.block, dragEdge.side);
    } else {
      const t = dropTarget(e.clientY);
      if (t) moveBlock(dragSrc, t.block, t.after);
    }
    dragSrc = null;
    dragEdge = null;
    dropInto = null;
    indicator.style.display = "none";
  }
  editorEl.addEventListener("drop", onDrop);

  return {
    element: menu,
    showFor,
    hide,
    addAfter,
    moveBlock,
    wrapInColumns,
    openDragMenu,
    closeDragMenu, // M1: 재구축(restore) 시 열린 드롭다운 닫기용
    destroy() {
      editorEl.removeEventListener("mousemove", onMove);
      editorEl.removeEventListener("mouseleave", onMouseLeave);
      editorEl.removeEventListener("dragover", onDragOver);
      editorEl.removeEventListener("drop", onDrop);
      document.removeEventListener("mousedown", onDocDown);
      if (menu.parentNode) menu.parentNode.removeChild(menu);
      if (indicator.parentNode) indicator.parentNode.removeChild(indicator);
      if (dragMenu.parentNode) dragMenu.parentNode.removeChild(dragMenu);
    },
  };
}
